import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailWorkMethod } from "../../../../../http/hrail/types/workMethods";
import { useHrailWorkMethodsApi } from "../../../../../http/hrail/useWorkMethods";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateWorkMethodModalDialogButton from "./CreateWorkMethodModalDialogButton";
import WorkMethodForm from "./WorkMethodForm";

type Props = {
  active?: boolean;
};

const WorkMethods: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "workMethods" });
  const aborter = useRef(new AbortController());
  const workMethodApi = useHrailWorkMethodsApi();
  const [itemToEdit, setItemToEdit] = useState<HrailWorkMethod>();
  const [itemToDelete, setItemToDelete] = useState<HrailWorkMethod>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isActiveTab = active ?? true;
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "workMethods");

  const updateVisibility = async (workMethod: HrailWorkMethod, visibility: VisibilityScopeValue) => {
    try {
      await workMethodApi.updateAdminHrailWorkMethodVisibility(workMethod.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (workMethod: HrailWorkMethod, newOrder: number) => {
    try {
      await workMethodApi.updateAdminHrailWorkMethodOrder(workMethod.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<HrailWorkMethod>("hrail.work_method");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = workMethodApi.useAdminHrailWorkMethods(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.workMethods.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.workMethods.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateWorkMethodModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailWorkMethod>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="right">{i18n.translation.common.span}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(workMethod) => (
          <>
            <Table.Td noWrap={false}>{workMethod.name ?? ""}</Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(workMethod.spanMm, i18n.translation.common.millimeters)}
            </Table.Td>
            <td align="center">
              <Visibility value={workMethod.visibility} onChange={(value) => updateVisibility(workMethod, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={workMethod.order} onUpdate={(newOrder) => updateOrder(workMethod, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(workMethod) => setItemToEdit(workMethod)}
        rowActions={(workMethod) => [
          getEditAction(i18n, () => setItemToEdit(workMethod)),
          accessesAction(workMethod),
          getDeleteAction(i18n, () => setItemToDelete(workMethod), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.workMethods.edit}>
          <WorkMethodForm workMethod={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.workMethods.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                workMethodApi
                  .deleteAdminHrailWorkMethod(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.workMethods.deleteLabel,
            itemToDelete.name || itemToDelete.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const formatNullableNumber = (value: number | null | undefined, suffix = "") =>
  typeof value === "number" ? String(value) + suffix : "";

export default WorkMethods;
